import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import {
	Button,
	FormGroup,
	Input,
	Modal,
	ModalContent,
	ModalTitle,
	SelectAdv,
} from "@lib/components";
import { withTranslation, WithTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FORMATS, Untrusive } from "@lib/common";
import { UI } from "../../../../../core/ui";
import isEmail from "validator/lib/isEmail";
import { BookingCustomerSearch } from "./booking-customer-search";
import { loadDashboardBookingTableOptions } from "./booking-modal-table-options";

function parseWalkInTableIds(tableKey: string): string[] | undefined {
	if (!tableKey) return undefined;
	try {
		const parsed = JSON.parse(tableKey) as string[];
		if (Array.isArray(parsed) && parsed.length) return parsed;
	} catch {
		/* ignore */
	}
	return undefined;
}

function WalkInTableSync({
	depKey,
	onSync,
}: {
	depKey: string;
	onSync: () => void;
}) {
	const cb = React.useRef(onSync);
	cb.current = onSync;
	React.useEffect(() => {
		cb.current();
	}, [depKey]);
	return null;
}

interface Props extends WithTranslation {
	open: boolean;
	onClose: () => void;
	onCreated: (booking: T.Schema.Booking.BookingSchema) => void;
}

interface State {
	name: string;
	phone: string;
	email: string;
	number_of_people: number;
	/** JSON.stringify(table_ids) from listMoveTableOptions, or "" for auto-assign */
	table_key: string;
	linkedCustomerId: string | null;
	notes: string;
	turn_minutes: number | "";
	tableOptions: Array<{ table_ids: string[]; label: string }>;
	tableLoading: boolean;
}

@inject("store")
@observer
export class RestaurantWalkInModalClass extends MobxComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			name: "",
			phone: "",
			email: "",
			number_of_people: 2,
			table_key: "",
			linkedCustomerId: null,
			notes: "",
			turn_minutes: "",
			tableOptions: [],
			tableLoading: false,
		};
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.open && !this.props.open) {
			this.setState({
				name: "",
				phone: "",
				email: "",
				number_of_people: 2,
				table_key: "",
				linkedCustomerId: null,
				notes: "",
				turn_minutes: "",
				tableOptions: [],
				tableLoading: false,
			});
		}
	}

	loadWalkInTableOptions = async () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const bl = r.settings.services.table_booking.booking_logic;
		const defaultTurn =
			bl?.default_turn_minutes && bl.default_turn_minutes > 0
				? bl.default_turn_minutes
				: 90;
		const raw = this.state.turn_minutes;
		const parsed =
			typeof raw === "string" && raw !== ""
				? parseInt(raw, 10)
				: typeof raw === "number"
					? raw
					: NaN;
		const turn =
			Number.isFinite(parsed) && parsed >= 15 ? parsed : defaultTurn;
		const ts = moment().tz(tz).valueOf();

		this.setState({ tableLoading: true });
		try {
			const opts = await loadDashboardBookingTableOptions(store, {
				restaurantId: r._id,
				timezone: tz,
				timestamp: ts,
				turnMinutes: turn,
				party: this.state.number_of_people,
			});
			this.setState({ tableOptions: opts, tableLoading: false });
		} catch {
			this.setState({ tableOptions: [], tableLoading: false });
		}
	};

	submit = async () => {
		const { store, t, onClose, onCreated } = this.injected;
		const {
			name,
			phone,
			email,
			number_of_people,
			table_key,
			linkedCustomerId,
			notes,
			turn_minutes,
		} = this.state;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const service = r.settings.services.table_booking;
		const maxPeople = service.max_people || service.limit_number_of_people;
		const minPeople = service.min_people;

		if (!name.trim()) {
			UI.notification.error(t("Name is required", { defaultValue: "Name is required" }));
			return;
		}
		if (!phone.trim()) {
			UI.notification.error(t("Phone is required", { defaultValue: "Phone is required" }));
			return;
		}
		if (linkedCustomerId) {
			if (!email.trim() || !isEmail(email, { require_tld: true })) {
				UI.notification.error(
					t("Valid email is required for linked customer", {
						defaultValue: "Valid email is required for linked customer",
					})
				);
				return;
			}
		}
		if (maxPeople || minPeople) {
			if (
				maxPeople &&
				minPeople &&
				(number_of_people > maxPeople || number_of_people < minPeople)
			) {
				UI.notification.error(
					t("Invalid party size", { defaultValue: "Invalid party size" })
				);
				return;
			}
			if (maxPeople && !minPeople && number_of_people > maxPeople) {
				UI.notification.error(
					t("Invalid party size", { defaultValue: "Invalid party size" })
				);
				return;
			}
			if (!maxPeople && minPeople && number_of_people < minPeople) {
				UI.notification.error(
					t("Invalid party size", { defaultValue: "Invalid party size" })
				);
				return;
			}
		}

		const ts = moment().tz(tz).valueOf();
		const m = moment(ts).tz(tz);

		const turnParsed =
			typeof turn_minutes === "string" && turn_minutes !== ""
				? parseInt(turn_minutes, 10)
				: typeof turn_minutes === "number"
					? turn_minutes
					: NaN;
		const dashboard_turn_minutes =
			Number.isFinite(turnParsed) && turnParsed >= 15
				? turnParsed
				: undefined;
		const walkInTableIds = parseWalkInTableIds(table_key);

		Untrusive.start();
		try {
			const res = await store.api.booking_create({
				restaurant_id: r._id,
				booking: {
					notes: notes.trim(),
					custom_booking_field_list: [],
					customer: linkedCustomerId
						? {
								_id: linkedCustomerId,
								name: name.trim(),
								phone: phone.trim(),
								email: email.trim(),
							}
						: {
								_id: "",
								name: name.trim(),
								phone: phone.trim(),
								email: `walk-in+${Date.now()}@guest.local`,
							},
					config: {
						date: m.format(FORMATS.moment.date),
						time: m.format(FORMATS.moment.time),
						timestamp: ts,
						number_of_people,
					},
					walk_in: true,
					dashboard_seat_now: true,
					...(dashboard_turn_minutes !== undefined
						? { dashboard_turn_minutes }
						: {}),
					...(walkInTableIds ? { walk_in_table_ids: walkInTableIds } : {}),
				},
			});
			if (res.outcome !== 0) {
				const msg = (res as { message?: string }).message || "create_failed";
				if (msg === "booking_capacity_threshold") {
					UI.notification.error(
						t("booking.error.capacity_threshold", {
							defaultValue:
								"Restaurant is at capacity for this time (smart threshold).",
						})
					);
				} else if (msg === "invalid_table_assignment") {
					UI.notification.error(
						t("booking.error.invalid_tables", {
							defaultValue:
								"Chosen table is not available for this time or party size.",
						})
					);
				} else {
					throw new Error(msg);
				}
				return;
			}
			if ("booking" in res && res.booking) {
				onCreated(res.booking);
			}
			UI.notification.success(
				t("Guest seated", { defaultValue: "Guest seated" })
			);
			this.setState({
				name: "",
				phone: "",
				email: "",
				number_of_people: 2,
				table_key: "",
				linkedCustomerId: null,
				notes: "",
				turn_minutes: "",
				tableOptions: [],
				tableLoading: false,
			});
			onClose();
		} catch {
			UI.notification.error(
				t("Could not seat guest", { defaultValue: "Could not seat guest" })
			);
		} finally {
			Untrusive.stop();
		}
	};

	render() {
		const { store, t, open, onClose } = this.injected;
		const {
			name,
			phone,
			email,
			number_of_people,
			table_key,
			linkedCustomerId,
			notes,
			turn_minutes,
		} = this.state;
		const r = store.restaurant!;
		const tables = r.settings.services?.dine_in?.tables || [];
		const defaultTurnMinutes =
			r.settings.services.table_booking.booking_logic?.default_turn_minutes &&
			r.settings.services.table_booking.booking_logic.default_turn_minutes > 0
				? r.settings.services.table_booking.booking_logic.default_turn_minutes
				: 90;

		const tableSelectOpts = this.state.tableOptions.map((o) => ({
			label: o.label,
			value: JSON.stringify(o.table_ids),
		}));

		const tableOpts = [
			{
				label: t("Auto-assign table", { defaultValue: "Auto-assign table" }),
				value: "",
			},
			...tableSelectOpts,
		];

		const syncDep = `${open}|${number_of_people}|${turn_minutes}|${tables.length}`;

		return (
			<Modal active={open} close={onClose} width="sm" alignTop>
				<WalkInTableSync depKey={syncDep} onSync={this.loadWalkInTableOptions} />
				<ModalTitle className="round-top">
					{t("Walk-in", { defaultValue: "Walk-in" })}
				</ModalTitle>
				<ModalContent paddinglr={20} paddingtb={16}>
					<p className="small text-muted m-b-3 m-t-0">
						{t("walkInHelp", {
							defaultValue:
								"Seat a guest now. Tables listed are free for the current time slot and fit your party.",
						})}
					</p>
					<BookingCustomerSearch
						active={open}
						restaurantId={r._id}
						showClear={!!linkedCustomerId}
						onPick={(p) => {
							this.setState({
								name: p.name,
								phone: p.phone,
								email: p.email,
								linkedCustomerId: p._id,
							});
						}}
						onClear={() => {
							this.setState({
								linkedCustomerId: null,
								email: "",
							});
						}}
					/>
					<FormGroup title={t("Name", { defaultValue: "Name" })}>
						<Input
							value={name}
							onChange={(e) => this.setState({ name: e.target.value })}
							autoComplete="name"
						/>
					</FormGroup>
					<FormGroup title={t("Phone", { defaultValue: "Phone" })}>
						<Input
							value={phone}
							onChange={(e) => this.setState({ phone: e.target.value })}
							autoComplete="tel"
						/>
					</FormGroup>
					{linkedCustomerId ? (
						<FormGroup
							title={t("Email", { defaultValue: "Email" })}
							help={t("walkInLinkedEmailHelp", {
								defaultValue: "From linked customer profile.",
							})}
						>
							<Input
								type="email"
								value={email}
								onChange={(e) => this.setState({ email: e.target.value })}
								autoComplete="email"
							/>
						</FormGroup>
					) : null}
					<FormGroup title={t("Party size", { defaultValue: "Party size" })}>
						<Input
							type="number"
							min={1}
							max={99}
							value={number_of_people}
							onChange={(e) =>
								this.setState({
									number_of_people: parseInt(e.target.value, 10) || 1,
								})
							}
						/>
					</FormGroup>
					<FormGroup
						optional
						title={t("Duration (minutes)", {
							defaultValue: "Duration (minutes)",
						})}
						help={t("walkIn.turnHelp", {
							defaultValue:
								"Leave blank to use default turn from settings ({{mins}} min).",
							mins: defaultTurnMinutes,
						})}
					>
						<Input
							type="number"
							min={15}
							step={5}
							placeholder={String(defaultTurnMinutes)}
							value={turn_minutes}
							onChange={(e) => {
								const v = e.target.value;
								this.setState({
									turn_minutes:
										v === "" ? "" : parseInt(v, 10) || "",
								});
							}}
						/>
					</FormGroup>
					{tables.length > 0 && (
						<FormGroup
							title={t("Table", { defaultValue: "Table" })}
							optional
							help={
								this.state.tableLoading
									? t("Loading tables…", { defaultValue: "Loading tables…" })
									: t("walkIn.tableHelp", {
											defaultValue:
												"Free tables and joinable combos from your floor plan; auto-assign picks the best fit.",
										})
							}
						>
							<SelectAdv
								type="single"
								shadow="one"
								value={table_key}
								onChange={(v: string) => this.setState({ table_key: v })}
								options={tableOpts}
							/>
						</FormGroup>
					)}
					<FormGroup
						optional
						title={t("Notes", { defaultValue: "Notes" })}
					>
						<Input
							value={notes}
							onChange={(e) => this.setState({ notes: e.target.value })}
							placeholder={t("Optional", { defaultValue: "Optional" })}
						/>
					</FormGroup>
					<div className="flex-line gap-2 m-t-3" style={{ gap: 8}}>
						<Button type="button" color="white" full onClick={onClose}>
							{t("Cancel", { defaultValue: "Cancel" })}
						</Button>
						<Button type="button" color="primary" full onClick={this.submit}>
							{t("Seat now", { defaultValue: "Seat now" })}
						</Button>
					</div>
				</ModalContent>
			</Modal>
		);
	}
}

export const RestaurantWalkInModal = withTranslation()(RestaurantWalkInModalClass);
